import { GetterTree } from 'vuex';
import {
  AddressBookItem,
  AvailableService,
  InProgress,
  PackageItem,
  ReconnectAccounts,
  ShipFrom,
  Shipment,
  ShipTo,
  StoreForImport,
} from '../types';
import { UploadDocument } from '../types/documentType';
import { PAPERLESS_TRADE_COUNTRIES } from '../constants/paperlessTradeCountries';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getters: GetterTree<Shipment, any> = {
  getIsInternational(state: Shipment): boolean {
    const { fromAddress, toAddress } = state.inProgress || {};
    const { countryCode: fromCountryCode } = fromAddress || {};
    const { countryCode: toCountryCode } = toAddress || {};
    return !!fromCountryCode && !!toCountryCode && fromCountryCode !== toCountryCode;
  },

  getIsCanadaDomesticShipment(state: Shipment): boolean {
    return state.inProgress.fromAddress.countryCode === 'CA'
      && state.inProgress.toAddress.countryCode === 'CA';
  },

  getIsCommercialInvoice(state: Shipment): boolean {
    const {
      fromAddress, toAddress, packages, documentsOnly,
    } = state.inProgress;

    const { countryCode: fromCountryCode } = fromAddress;
    const { countryCode: toCountryCode } = toAddress;

    if (!fromCountryCode || !toCountryCode) return false;

    if (fromCountryCode !== toCountryCode) {
      if (!packages.length) return false;

      if (documentsOnly) return false;

      if (packages.length === 1 && packages[0].packageType === 'Envelope') return false;

      return true;
    }

    return false;
  },

  getPackageSummary(state: Shipment): Array<PackageItem> {
    return state.inProgress.packages;
  },

  getAddressBook(state: Shipment): Array<AddressBookItem> {
    return state.addressBook;
  },

  getSelectedService(state: Shipment): AvailableService | null {
    return state.selectedService;
  },

  getAvailableServices(state: Shipment): Array<AvailableService> | null {
    return state.availableServices;
  },

  getReconnectAccountRequired(state: Shipment): ReconnectAccounts | null {
    return state.reconnectAccountRequired;
  },

  getInProgress(state: Shipment): InProgress | null {
    return state.inProgress;
  },

  getShipFromInProgress(state: Shipment): ShipFrom | undefined {
    return state.inProgress?.fromAddress;
  },

  getShipToInProgress(state: Shipment): ShipTo | undefined {
    return state.inProgress?.toAddress;
  },

  getIsInProgressData(state: Shipment): number {
    return state.isInProgressData;
  },

  getPackagesInProgress(state: Shipment): Array<PackageItem> | undefined {
    return state.inProgress?.packages;
  },

  getStoresForImport(state: Shipment): Array<StoreForImport> {
    return state.storesForImport;
  },

  getSecureshipTrackingNo(state: Shipment): string {
    return state.secureshipTrackingNo;
  },

  getIsWorkingOnShipment(state: Shipment, _, __, rootGetters) {
    const {
      toAddress,
      fromAddress,
    } = state.inProgress;
    const { shippingSettings } = rootGetters.getUserPreferences;

    const toAddressWithoutChange = Object.values(toAddress).every(
      (x) => x === null || x === '' || x === undefined || x === false || x.length === 0,
    );

    const fromAddressWithoutChange = Object.entries(fromAddress).every(
      ([key, value]) => {
        if (shippingSettings.defaultAddress) {
          return shippingSettings.defaultAddress[key] === value;
        }

        return false;
      },
    );

    return !toAddressWithoutChange || !fromAddressWithoutChange;
  },

  getUploadDocuments(state: Shipment): Array<UploadDocument> {
    return state.inProgress.uploadDocuments || [];
  },

  hasProcessingDocuments(state: Shipment): boolean {
    const docs = state.inProgress.uploadDocuments;
    if (!docs || !docs.length) return false;
    return docs.some((doc) => doc.status === 'Processing');
  },

  hasDocumentsWithoutType(state: Shipment): boolean {
    const docs = state.inProgress.uploadDocuments;
    if (!docs || !docs.length) return false;
    return docs.some((doc) => !doc.documentType || doc.documentType === 'NotSet');
  },

  isPaperlessTradeSupported(state: Shipment): boolean {
    const { countryCode } = state.inProgress.toAddress || {};
    if (!countryCode) return false;
    const support = PAPERLESS_TRADE_COUNTRIES[countryCode];
    if (!support) return false;
    return support.etd || support.ewo;
  },
};

export default getters;
